/* eslint-disable @next/next/no-img-element */
/** @jsxImportSource @emotion/react */
/**
 * DayViewAvailability.js
 * Day-view arm of booking step 3 (behind day-view-web): a day picker plus a grid of time slots
 * grouped Morning / Afternoon / Night. Selecting an available slot adapts the fullDayAvailability
 * result into the selectedAppointment shape the rest of the flow consumes. Alternate (partial) slots
 * render as a card (time + reduced-service label + sparkle icon) and, when tapped, book the reduced set.
 * The grid is padded from the market booking window so the slot count is consistent across days, and
 * a Morning/Afternoon/Night section with no bookable slots starts collapsed.
 */
import React, { useState, useEffect } from 'react'
import { css } from '@emotion/react'
import { DateTime } from 'luxon'
import { useFullDayAvailability, useAllMarketServices } from '../../../../services/GraphQlClientService'
import {
  buildDayView,
  SLOT_VARIANT,
  dayViewSlotToSelectedAppointment,
} from '../../../../helpers/dayViewAvailability'
import {
  useBooleanState,
  shiftDate,
  serviceAndAddOnServiceNames,
  trackDayViewAvailabilitySearch,
  trackDayViewAvailabilitySearchError,
  trackDayViewDateSelected,
  trackDayViewSlotSelected,
  mq,
} from '../../../../helpers'
import CalendarDropdown from '../../../CalendarDropdown'
import LoadingDot from './LoadingDot'
import { Header2, Text } from '../../../../styles/Components'
import { gsColors } from '../../../../styles/Variables'

const SECTIONS = [['morning', 'Morning'], ['afternoon', 'Afternoon'], ['night', 'Night']]

const containerCss = css`
  max-width: 738px;
  margin: 0 auto;
  padding: 64px 24px;

  ${mq('max', '768px')} {
    padding: 64px 16px;
  }
`
const headerCss = css`
  text-align: center;
  margin-bottom: 24px;

  ${mq('max', '768px')} {
    h2 { font-size: 28px !important; }
    p { font-size: 14px !important; line-height: 20px; }
  }
`
const dayLabelCss = css`display: block; font-family: Euclid; color: ${gsColors.mediumGrey}; font-size: 12px; letter-spacing: 0.24px;`
const myServicesCss = css`margin-top: 24px;`
const chipRowCss = css`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-top: 12px;
`
const chipCss = css`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 21px;
  background: #f5f5f5;
  font-family: Euclid;
  font-size: 14px;
  line-height: normal;
  color: ${gsColors.darkGrey};
`
const sectionCss = css`margin-top: 40px;`
const sectionHeaderCss = css`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  background: none;
  border: 0;
  padding: 0 0 16px;
  cursor: pointer;
  font-family: Euclid;
  font-weight: 600;
  font-size: 18px;
  line-height: 1.38;
  color: ${gsColors.black};

  ${mq('max', '768px')} {
    font-size: 16px;
  }

  img { width: 24px; height: 24px; }
`
const gridCss = css`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  ${mq('max', '768px')} {
    grid-template-columns: repeat(3, 1fr);
  }

  ${mq('max', '374px')} {
    grid-template-columns: repeat(2, 1fr);
  }
`
// Every slot is the same fixed size: the time sits top-left, and an alternate slot only *adds* the
// top-right sparkle and the bottom-left label — it never changes the card's dimensions.
const slotCss = css`
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: 4px;
  height: 56px;
  padding: 8px;
  border: 1px solid ${gsColors.darkGrey};
  border-radius: 4px;
  background: ${gsColors.white};
  color: ${gsColors.black};
  font-family: Euclid;
  cursor: pointer;
  overflow: hidden;

  &:hover:not(:disabled):not(.selected) {
    border-color: ${gsColors.black};
  }

  &.selected {
    background: ${gsColors.black};
    border-color: ${gsColors.black};
    color: ${gsColors.white};
  }

  &:disabled {
    background: #ededed;
    border-color: transparent;
    color: ${gsColors.mediumGrey};
    cursor: default;
  }
`
const slotTimeCss = css`
  text-align: left;
  white-space: nowrap;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0.16px;
`
// pinned top-right so it never steals width from the time (which would wrap on narrow mobile cards)
const sparkleCss = css`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 12px;
  height: 12px;
`
const altLabelCss = css`
  max-width: 100%;
  font-size: 11px;
  line-height: 16px;
  color: ${gsColors.mediumGrey};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .selected & {
    color: ${gsColors.white};
  }
`
const emptyCss = css`margin-top: 32px; text-align: center; color: ${gsColors.darkGrey};`
// heal/search errors read as errors (red), distinct from the grey "no appointments" empty state
const errorCss = css`margin-top: 24px; text-align: center; color: ${gsColors.errorRed};`

/**
 * DayViewSlot: one fixed-size time-slot card. Its look and behavior switch on the slot's variant —
 * available (white, time only), alternate (white, time + sparkle top-right + reduced-service label),
 * unavailable (grey, disabled), and selected (black). Alternate/available are tappable.
 * @param {object} props { slotVM, selected, onSelect }
 * @returns {JSX.Element}
 */
const DayViewSlot = ({ slotVM, selected, onSelect }) => {
  const isAlternate = slotVM.variant === SLOT_VARIANT.ALTERNATE
  return (
    <button
      type="button"
      data-variant={slotVM.variant}
      disabled={slotVM.variant === SLOT_VARIANT.UNAVAILABLE}
      aria-pressed={selected}
      className={selected ? 'selected' : undefined}
      css={slotCss}
      onClick={() => onSelect(slotVM)}
    >
      <span css={slotTimeCss}>{slotVM.label}</span>
      {isAlternate && slotVM.alternateLabel && <span css={altLabelCss}>{slotVM.alternateLabel}</span>}
      {isAlternate && (
        <svg css={sparkleCss} viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path
            d="M7.50098 4.26562L7.57715 4.42285L7.73438 4.49902L10.8486 6L7.73438 7.50098L7.57715 7.57715L7.50098 7.73438L6 10.8486L4.49902 7.73438L4.42285 7.57715L4.26562 7.50098L1.15039 6L4.26562 4.49902L4.42285 4.42285L4.49902 4.26562L6 1.15039L7.50098 4.26562Z"
            stroke="currentColor"
          />
          <path d="M4.04839 7.95161L6 12V0L4.04839 4.04839L0 6L4.04839 7.95161Z" fill="currentColor" />
        </svg>
      )}
    </button>
  )
}

/**
 * DayViewAvailability: renders the day-view booking grid (day picker + Morning/Afternoon/Night slot
 * sections) for booking step 3, and dispatches `{ selectedAppointment, startDateTime }` when the
 * shopper taps an available slot.
 * @param {object} props { state, dispatch, setIsStepValid, me }
 * @returns {JSX.Element}
 */
const DayViewAvailability = ({ state, dispatch, setIsStepValid }) => {
  const { market, address, serviceRequests, selectedAppointment, bookingHealMessage } = state
  const { id: marketId, timezone, minimumAdvanceBookingMinutes = 0, firstAppointmentTime, lastAppointmentTime } = market
  const now = DateTime.local().setZone(timezone)

  const [calendarOpen, toggleCalendar] = useBooleanState()
  // Returning from a later step, `selectedAppointment` is still set — open on its day so the grid shows
  // (and highlights) the selected slot instead of always resetting to today.
  const [selectedDate, setSelectedDate] = useState(
    selectedAppointment?.actualStartDateTime
      ? DateTime.fromSeconds(selectedAppointment.actualStartDateTime).setZone(timezone).startOf('day')
      : now.startOf('day'),
  )
  const [openSections, setOpenSections] = useState({ morning: true, afternoon: true, night: true })

  const marketServices = useAllMarketServices(marketId) || []
  const dateString = selectedDate.toFormat('yyyy-MM-dd')
  const [slots, error] = useFullDayAvailability({ date: dateString, address, serviceRequests })
  const dayViewOptions = {
    timezone,
    now,
    dayStart: selectedDate,
    minimumAdvanceBookingMinutes,
    firstAppointmentTime,
    lastAppointmentTime,
    serviceRequests,
    marketServices,
  }
  // `slots` lags a day change by one render: useFullDayAvailability clears its value in an effect (after
  // paint), so for one frame it still holds the previous day's slots. Building the grid from those would
  // map stale booking tokens onto the new day's positions and let a tap in that window book the wrong
  // date — so only build once the slots belong to the selected day (an empty day carries no tokens).
  const slotsMatchSelectedDay =
    !!slots &&
    (slots.length === 0 || DateTime.fromISO(slots[0].startDateTime, { zone: timezone }).hasSame(selectedDate, 'day'))
  const view = slotsMatchSelectedDay ? buildDayView(slots, dayViewOptions) : null

  // the unique services (each with its add-ons) in this booking, for the "MY SERVICES" chips
  const serviceLabels = [
    ...new Set(
      serviceRequests
        .map(sr => {
          const { serviceName, addOnServiceNames = [] } = serviceAndAddOnServiceNames(marketServices, {
            serviceId: sr.serviceId,
            addOnServiceIds: sr.addOnServiceIds || [],
          })
          return serviceName ? serviceName + addOnServiceNames.filter(Boolean).map(name => ` + ${name}`).join('') : null
        })
        .filter(Boolean),
    ),
  ]

  setIsStepValid(!!selectedAppointment, 'Account Details')

  const blockHasBookable = section => view[section].some(slot => slot.variant !== SLOT_VARIANT.UNAVAILABLE)

  // One search event per day, once market services have loaded (they resolve the `products` the event
  // shares with the classic search). address/cart are fixed for this step's mounted lifetime, so the
  // day is the only search input that changes here.
  useEffect(() => {
    if (!marketServices.length) {
      return
    }
    trackDayViewAvailabilitySearch({ market, date: dateString, serviceRequests, address, marketServices })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateString, marketServices.length])

  // A failed full-day request only reaches `error`; emit the error outcome (parity with the classic
  // search's catch path) so a failure isn't miscounted as an abandoned search.
  useEffect(() => {
    if (!error) {
      return
    }
    trackDayViewAvailabilitySearchError({
      market, date: dateString, serviceRequests, address, marketServices,
      errorMessage: error?.message,
      isUserFacing: !!error?.isUserFacing,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  // collapse a section when a day's results arrive with nothing bookable in it
  useEffect(() => {
    if (view) {
      setOpenSections({ morning: blockHasBookable('morning'), afternoon: blockHasBookable('afternoon'), night: blockHasBookable('night') })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots])

  const selectSlot = slotVM => {
    const isAlternate = slotVM.variant === SLOT_VARIANT.ALTERNATE
    if (slotVM.variant !== SLOT_VARIANT.AVAILABLE && !isAlternate) {
      return
    }

    // An alternate slot selects its partial appointment object and continues just like a regular slot —
    // no re-search, no cart change. The bookingTokens/price on the appointment are authoritative.
    const bookedAppointment = isAlternate ? slotVM.partialAppointment : slotVM.appointment
    const selected = dayViewSlotToSelectedAppointment(bookedAppointment, slotVM.startDateTime, {
      marketId, marketShortName: market.shortName, timezone, addressId: address.id,
    })
    dispatch({
      selectedAppointment: selected,
      startDateTime: DateTime.fromSeconds(selected.actualStartDateTime).setZone(timezone),
      bookingHealMessage: null,
    })
    trackDayViewSlotSelected({
      time: slotVM.label,
      isAlternate,
    })
  }

  const changeDay = date => {
    const nextDate = DateTime.fromJSDate(date).setZone(timezone).startOf('day')
    setSelectedDate(nextDate)
    dispatch({ selectedAppointment: false, bookingHealMessage: null })
    toggleCalendar()
    trackDayViewDateSelected({ date: nextDate.toFormat('yyyy-MM-dd'), address, serviceRequests, marketServices })
  }

  const isSelected = slotVM =>
    !!selectedAppointment &&
    selectedAppointment.actualStartDateTime === Math.floor(slotVM.startDateTime.toSeconds())

  return (
    <div css={containerCss}>
      <div css={headerCss}>
        <Header2 align="center" margin="0 0 12px">When do you want to book?</Header2>
        <Text textAlign="center" color={gsColors.darkGrey} fontSize={18} lineHeight="26px" margin="0">
          Pick a time and we&apos;ll match you with a top-rated pro in your area.
        </Text>
      </div>

      <label css={dayLabelCss}>DAY</label>
      <CalendarDropdown
        date={shiftDate(selectedDate, timezone, -1)}
        onClick={date => changeDay(shiftDate(date, timezone, 1))}
        open={calendarOpen}
        toggle={toggleCalendar}
        timezone={timezone}
        format
      />

      {serviceLabels.length > 0 && (
        <div css={myServicesCss}>
          <label css={dayLabelCss}>MY SERVICES</label>
          <div css={chipRowCss}>
            {serviceLabels.map(label => (
              <span key={label} css={chipCss}>{label}</span>
            ))}
          </div>
        </div>
      )}

      {(bookingHealMessage || error) && (
        <p css={errorCss} role="alert">{bookingHealMessage || 'Something went wrong loading availability. Please try again.'}</p>
      )}
      {!view && !error && <LoadingDot margin="40px auto 0" />}
      {view && !view.hasBookable && <p css={emptyCss}>No appointments available on this day.</p>}

      {view && view.hasBookable && SECTIONS.map(([key, label]) =>
        view[key].length ? (
          <section key={key} css={sectionCss}>
            <button
              type="button"
              css={sectionHeaderCss}
              aria-expanded={openSections[key]}
              onClick={() => setOpenSections(s => ({ ...s, [key]: !s[key] }))}
            >
              {label}
              <img
                src={openSections[key] ? '/images/nav-arrow-up.svg' : '/images/nav-arrow-down.svg'}
                alt=""
                aria-hidden="true"
              />
            </button>
            {openSections[key] && (
              <div css={gridCss}>
                {view[key].map(slotVM => (
                  <DayViewSlot
                    key={slotVM.startDateTime.toISO()}
                    slotVM={slotVM}
                    selected={isSelected(slotVM)}
                    onSelect={selectSlot}
                  />
                ))}
              </div>
            )}
          </section>
        ) : null,
      )}
    </div>
  )
}

export default DayViewAvailability
